import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Contract, parseEther } from "ethers";
import { useWallet } from "@/context/WalletContext";
import { usePrice, usePolling } from "@/components/croc/hooks";
import { Panel, SectionTitle, Pill } from "@/components/croc/ui";
import { fmtNum, fmtUsd, pct } from "@/lib/format";
import api from "@/lib/api";
import { CONTRACTS, ABIS, CHAIN, CROC_PER_BNB } from "@/contracts/croc";
import { ExchangePanel } from "@/components/croc/ExchangePanel";
import { ArrowDown, Zap, Info, Wallet, Link2, CheckCircle2, Cpu, Coins } from "lucide-react";

export default function BuyStake() {
  const { address, balance, refreshUser, isDemo, getBrowserProvider, ensureNetwork, isTargetChain } = useWallet();
  const price = usePrice();
  const navigate = useNavigate();
  const { data: config } = usePolling(() => api.config(), [], 120000);
  const [mode, setMode] = useState(isDemo ? "sim" : "onchain");
  const [amount, setAmount] = useState("500");
  const [tierKey, setTierKey] = useState("360d");
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null);

  const tiers = config?.tiers ?? [];
  const tier = tiers.find((t) => t.key === tierKey);
  const tierIndex = tiers.findIndex((t) => t.key === tierKey);
  const bnbPrice = price?.reference?.price || 700;
  const onchain = mode === "onchain" && !isDemo;

  const num = parseFloat(amount) || 0;
  const usd = num;
  const crocAmount = price?.price ? num / price.price : 0;
  const dailyReward = tier ? usd * tier.daily_rate : 0;

  const submitSim = async () => {
    if (usd < 25) return toast.error("Minimum stake is $25");
    const res = await api.stake({ address, tier_key: tierKey, amount_usd: usd });
    toast.success("Auto-staked (simulated)", { description: `${fmtNum(res.croc_amount)} CROC locked in ${tier?.name}.` });
    await refreshUser();
    navigate("/tiers");
  };

  const submitOnchain = async () => {
    if (num <= 0) return toast.error("Enter a USDT amount");
    const provider = getBrowserProvider();
    if (!provider) return toast.error("Connect a real wallet (MetaMask/WalletConnect) for on-chain staking");
    if (!isTargetChain) {
      const ok = await ensureNetwork();
      if (!ok) return toast.error(`Switch your wallet to ${CHAIN.name}`);
    }
    const signer = await provider.getSigner();
    const me = await signer.getAddress();
    const usdtAbi = ["function approve(address,uint256) returns (bool)", "function allowance(address,address) view returns (uint256)"];
    const usdt = new Contract(CONTRACTS.MockUSDT, usdtAbi, signer);
    const wei = parseEther(String(num));
    const allowance = await usdt.allowance(me, CONTRACTS.CrocExchange);
    if (allowance < wei) {
      toast("Approve USDT spending…");
      await (await usdt.approve(CONTRACTS.CrocExchange, wei)).wait();
    }
    const ex = new Contract(CONTRACTS.CrocExchange, ABIS.CrocExchange, signer);
    toast("Confirm the purchase in your wallet…");
    const tx = await ex.buy(wei, tierIndex);
    toast.loading("Transaction submitted, waiting for confirmation…", { id: "tx" });
    const rcpt = await tx.wait();
    toast.dismiss("tx");
    toast.success("USDT purchase + auto-stake confirmed", { description: `Block ${rcpt.blockNumber}` });
    setReceipt({ hash: tx.hash, croc: crocAmount, bnb: num });
    try { await api.stake({ address, tier_key: tierKey, amount_usd: usd }); } catch (_) {}
    await refreshUser();
  };

  const submit = async () => {
    try {
      setLoading(true);
      if (onchain) await submitOnchain();
      else await submitSim();
    } catch (e) {
      toast.dismiss("tx");
      const msg = e?.response?.data?.detail || e?.shortMessage || e?.message || "Transaction failed";
      toast.error(msg.slice(0, 120));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SectionTitle
        eyebrow="DEX Buy & Auto-Stake Terminal"
        title="Buy & Auto-Stake"
        subtitle="Swap into CROC at the live rate and lock it into your chosen tier in one transaction."
        right={
          !isDemo && (
            <div className="flex rounded-xl border border-white/10 bg-[#090E0B] p-1" data-testid="buy-mode-toggle">
              {[["onchain", "On-chain"], ["sim", "Simulated"]].map(([k, label]) => (
                <button
                  key={k}
                  data-testid={`buy-mode-${k}`}
                  onClick={() => { setMode(k); setAmount("500"); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${mode === k ? "bg-[#1CFD4C] text-[#050806]" : "text-muted-foreground"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )
        }
        testid="buystake-title"
      />

      {onchain && (
        <div className="croc-panel p-3 mb-4 flex items-center gap-2 text-xs text-muted-foreground" data-testid="onchain-banner">
          <Cpu className="w-4 h-4 emerald-text" /> On-chain mode — real TUSDT transaction on {CHAIN.name}. CrocExchange:
          <a className="emerald-text hover:underline font-num" target="_blank" rel="noreferrer" href={`${CHAIN.explorer}/address/${CONTRACTS.CrocExchange}`}>
            {CONTRACTS.CrocExchange.slice(0, 8)}…{CONTRACTS.CrocExchange.slice(-6)}
          </a>
        </div>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        <Panel className="lg:col-span-3" glow="gold" data-testid="buystake-terminal">
          <div className="rounded-2xl border border-white/10 bg-[#090E0B] p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">You pay (USDT)</span>
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Wallet className="w-3 h-3" /> {balance ?? "0"} BNB gas
              </span>
            </div>
            <div className="flex items-center gap-3">
              <input
                data-testid="buy-amount-input"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="bg-transparent text-3xl font-num font-bold text-white w-full outline-none"
                placeholder="0"
              />
              <span className="font-num font-bold text-white shrink-0">USDT</span>
            </div>
            <div className="flex gap-2 mt-3">
              {[100, 500, 2500, 10000].map((v) => (
                <button
                  key={v}
                  data-testid={`buy-quick-${v}`}
                  onClick={() => setAmount(String(v))}
                  className="rounded-lg border border-white/10 px-3 py-1 text-xs text-muted-foreground hover:border-[#1CFD4C]/50 hover:text-white transition-colors font-num"
                >
                  {`$${v.toLocaleString()}`}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-center my-3">
            <div className="w-10 h-10 rounded-xl bg-[#0F1712] border border-[#1CFD4C]/30 grid place-items-center glow-gold">
              <ArrowDown className="w-4 h-4 gold-text" />
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#090E0B] p-5">
            <span className="text-xs text-muted-foreground">You receive & auto-stake</span>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-3xl font-num font-bold emerald-text w-full">{fmtNum(crocAmount, 2)}</span>
              <span className="font-num font-bold text-white shrink-0">CROC</span>
            </div>
            <div className="text-xs text-muted-foreground mt-2 font-num">
              `1 CROC = $${fmtNum(price?.price, 5)} · pay in USDT (BEP20)`
            </div>
          </div>

          <div className="mt-5 space-y-2.5 text-sm">
            <Row label="Selected tier" value={tier?.name} />
            <Row label="Lock duration" value={`${tier?.days} days`} />
            <Row label="Est. daily yield" value={pct((tier?.daily_rate ?? 0) * 100)} accent />
            <Row label="Est. daily reward" value={fmtUsd(dailyReward)} accent />
            <Row label="Direct sponsor bonus" value={tier?.sponsor_bonus ? "5% (to sponsor)" : "Excluded (24h)"} />
          </div>

          <button
            data-testid="buy-autostake-submit-button"
            onClick={submit}
            disabled={loading}
            className="mt-5 w-full flex items-center justify-center gap-2 rounded-xl bg-[#1CFD4C] py-3.5 font-bold text-[#050806] hover:bg-[#4DFF74] transition-colors disabled:opacity-60 glow-gold"
          >
            <Zap className="w-4 h-4" /> {loading ? "Processing…" : onchain ? "Approve & Stake On-chain" : "Approve & Auto-Stake"}
          </button>

          {receipt && (
            <div className="mt-4 rounded-xl border border-[#10B981]/25 bg-[#10B981]/[0.05] p-4" data-testid="onchain-receipt">
              <div className="flex items-center gap-2 font-display font-bold text-white mb-2">
                <CheckCircle2 className="w-4 h-4 emerald-text" /> On-chain stake confirmed
              </div>
              <div className="text-sm text-muted-foreground">Minted & staked {fmtNum(receipt.croc)} CROC for {receipt.bnb} USDT.</div>
              <a
                data-testid="tx-explorer-link"
                href={`${CHAIN.explorer}/tx/${receipt.hash}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 text-sm emerald-text hover:underline font-num"
              >
                <Link2 className="w-3.5 h-3.5" /> View transaction on BscScan
              </a>
            </div>
          )}
        </Panel>

        <div className="lg:col-span-2 space-y-3">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Choose staking tier</div>
          {tiers.map((t) => (
            <button
              key={t.key}
              data-testid={`buy-tier-${t.key}`}
              onClick={() => setTierKey(t.key)}
              className={`w-full text-left rounded-xl p-4 transition-all ${
                tierKey === t.key ? "shimmer-border glow-emerald" : "croc-panel hover:border-[#1CFD4C]/40"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-display font-bold text-white">{t.name}</span>
                <Pill tone={tierKey === t.key ? "emerald" : "neutral"}>{t.days === 1 ? "24h" : `${t.days}d`}</Pill>
              </div>
              <div className="flex items-center justify-between mt-2 text-xs">
                <span className="text-muted-foreground">{t.key} lock · {t.tagline}</span>
                <span className="font-num gold-text">{pct(t.daily_rate * 100)}/day</span>
              </div>
            </button>
          ))}
          <div className="croc-panel p-4 flex items-start gap-2">
            <Info className="w-4 h-4 gold-text shrink-0 mt-0.5" />
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Yields are variable and revenue-backed. Each tier pays a fixed daily rate (0.1%–0.5%/day), hard-capped at 0.5%/day.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <ExchangePanel tierIndex={tierIndex} />
        <div className="croc-panel p-6 flex items-start gap-3" data-testid="usdt-note">
          <Coins className="w-5 h-5 gold-text shrink-0 mt-0.5" />
          <p className="text-[13px] text-muted-foreground leading-relaxed">
            All purchases settle in <span className="text-white font-medium">USDT (BEP20)</span>. Selling burns your CROC and
            returns USDT at the live oracle rate after the admin deduction — the burn is provable on BscScan. Approve once,
            then buy or sell in a single transaction.
          </p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-num font-medium ${accent ? "emerald-text" : "text-white"}`}>{value}</span>
    </div>
  );
}
